import React, { useEffect, useState } from "react";
import { Gift, ChevronRight, Sparkles } from "lucide-react";
import { api } from "../../lib/api";
import CardRewardModal from "../CardRewardModal";

/**
 * DailyChartCard — the 7-day Daily Rewards Chart.
 *
 * Renders the seven-tile rarity ladder, highlights the current day, and
 * fires the reveal modal (with confetti + tiered audio) on claim.
 *
 * Forgiveness: there's no streak loss for missing a day. The chart sits
 * still until the user comes back. The only constraint is "one claim per
 * UTC day" — so a user can't drain the whole ladder in a single sitting.
 */
const RARITY_TINT = {
  common: "#94a3b8",
  uncommon: "#00ff88",
  rare: "#22d3ee",
  mythic: "#a78bfa",
  classified: "#ff3b30",
  divine: "#ffd700",
  celestial: "#d946ef",
};
const RARITY_LABEL = {
  common: "COMMON", uncommon: "UNCOMMON", rare: "RARE",
  mythic: "MYTHIC", classified: "CLASSIFIED", divine: "DIVINE",
  celestial: "CELESTIAL",
};

export default function DailyChartCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reward, setReward] = useState(null);
  const [showReward, setShowReward] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/daily-chart/state");
      setState(r.data || null);
    } catch { /* silent — user might be logged out */ }
  };

  useEffect(() => { refresh(); }, []);

  const onClaim = async () => {
    if (busy || !state?.can_claim_today) return;
    setBusy(true);
    try {
      const r = await api.post("/daily-chart/claim");
      const c = r.data?.card;
      if (c) {
        setReward(c);
        setShowReward(true);
      }
      await refresh();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Claim failed";
      // eslint-disable-next-line no-alert
      alert(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!state || !state.signed_in) return null;

  const ladder = state.ladder || [];
  const currentDay = state.current_day || 1;
  const canClaim = !!state.can_claim_today;
  const cycles = state.cycles_completed || 0;

  return (
    <div
      data-testid="daily-chart-card"
      className="mt-3 border border-[#ffd60a]/40 bg-[#0a0a0a] bg-gradient-to-br from-[#ffd60a]/[0.04] to-[#a78bfa]/[0.03] p-3"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Gift size={13} className="text-[#ffd60a]" />
          <span className="font-mono text-[10px] tracking-[0.25em] text-[#ffd60a]">
            DAILY REWARDS · 7-DAY LADDER
          </span>
        </div>
        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
          {cycles > 0 ? `${cycles}× CYCLE${cycles === 1 ? "" : "S"} COMPLETED` : "MISSED DAYS DON'T RESET"}
        </span>
      </div>

      {/* 7-tile ladder. The current day pulses; past days in this cycle are
          dimmed; the Celestial finale gets an iridescent border tint to
          telegraph the prize at the top. */}
      <div className="grid grid-cols-7 gap-1.5 mb-3">
        {ladder.map(({ day, rarity }) => {
          const isCurrent = day === currentDay;
          const isPast = day < currentDay;
          const color = RARITY_TINT[rarity] || "#94a3b8";
          return (
            <div
              key={day}
              data-testid={`daily-chart-day-${day}`}
              className={`relative border p-2 flex flex-col items-center transition-all ${
                isCurrent ? "bg-[#0a0a0a]" : isPast ? "opacity-40 bg-[#0a0a0a]/40" : "bg-[#0a0a0a]/70"
              }`}
              style={{
                borderColor: isCurrent ? color : `${color}55`,
                boxShadow: isCurrent ? `0 0 18px -6px ${color}aa` : "none",
              }}
            >
              <span className="font-mono text-[8px] tracking-[0.2em] text-zinc-500">D{day}</span>
              {rarity === "celestial" ? (
                <span
                  className="font-mono text-[8px] tracking-[0.18em] font-bold mt-0.5 truncate w-full text-center vc-rainbow-text"
                  title={RARITY_LABEL[rarity]}
                >
                  {RARITY_LABEL[rarity].slice(0, 4)}
                </span>
              ) : (
                <span
                  className="font-mono text-[8px] tracking-[0.18em] font-bold mt-0.5 truncate w-full text-center"
                  style={{ color }}
                  title={RARITY_LABEL[rarity]}
                >
                  {RARITY_LABEL[rarity].slice(0, 4)}
                </span>
              )}
              {day === 7 && (
                <Sparkles size={9} className="text-[#d946ef] mt-0.5" />
              )}
            </div>
          );
        })}
      </div>

      {/* Claim button + status line */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-zinc-400 text-xs">
          {canClaim ? (
            <>
              Next up: <span className="font-mono tracking-[0.18em] text-white">DAY {currentDay}</span>
              {" — "}
              <strong style={{ color: RARITY_TINT[state.current_rarity] }}>
                {RARITY_LABEL[state.current_rarity]}
              </strong>
              {" card"}
            </>
          ) : (
            <span className="font-mono text-[10px] tracking-[0.2em] text-zinc-500">
              CLAIMED · NEXT CLAIM AT UTC MIDNIGHT
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={onClaim}
          disabled={!canClaim || busy}
          data-testid="daily-chart-claim-btn"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] tracking-[0.2em] uppercase transition-all ${
            canClaim
              ? "bg-[#ffd60a] text-[#050505] hover:bg-white"
              : "border border-white/10 text-zinc-600 cursor-not-allowed"
          }`}
        >
          {busy ? "CLAIMING…" : canClaim ? "CLAIM TODAY" : "CLAIMED"}
          {canClaim && !busy && <ChevronRight size={11} />}
        </button>
      </div>

      {reward && (
        <CardRewardModal
          open={showReward}
          reward={reward}
          source="daily_chart"
          onClose={() => setShowReward(false)}
        />
      )}
    </div>
  );
}
